// EmployeeSearchPicker — reusable smart-search component used across every
// HR/Payroll/Assets/Leave/Performance form so users never have to type an Employee ID.
import React, { useEffect, useRef, useState } from 'react';
import { Search, X, UserCheck } from 'lucide-react';
import api from '@/lib/api';

export default function EmployeeSearchPicker({ value, onSelect, label = 'Select employee', placeholder = 'Search by name, employee #, email or phone…', testid = 'employee-picker', required = false }) {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  // Load selected employee details when value (id) is passed in but selected isn't set
  useEffect(() => {
    let cancel = false;
    (async () => {
      if (value && (!selected || selected.id !== value)) {
        try {
          const { data } = await api.get(`/hr/employees`, { params: { limit: 1, q: value } });
          const found = (data.items || []).find((e) => e.id === value);
          if (!cancel && found) setSelected(found);
        } catch (e) { /* ignore */ }
      }
      if (!value && selected) setSelected(null);
    })();
    return () => { cancel = true; };
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced server search
  useEffect(() => {
    if (!q || q.length < 1) { setItems([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try { const { data } = await api.get('/hr/employees/search', { params: { q, limit: 10 } }); setItems(data.items || []); }
      catch (e) { setItems([]); }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  // Click-outside close
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = (e) => {
    setSelected(e);
    setQ('');
    setOpen(false);
    onSelect?.(e);
  };
  const clear = () => {
    setSelected(null);
    setQ('');
    onSelect?.(null);
  };

  return (
    <div ref={boxRef} className="relative" data-testid={testid}>
      <label className="block text-[11px] uppercase tracking-wider text-neutral-600 font-semibold mb-1.5">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {selected ? (
        <div className="flex items-center justify-between px-3 py-2 border-2 border-emerald-200 bg-emerald-50/40 rounded-md">
          <div className="flex items-center gap-2.5 min-w-0">
            <UserCheck className="w-4 h-4 text-emerald-600 flex-shrink-0" />
            <div className="min-w-0">
              <div className="font-medium text-sm truncate">{selected.first_name} {selected.last_name}</div>
              <div className="text-[11px] text-neutral-500 font-mono truncate">{selected.employee_no || selected.employee_code} · {selected.email || selected.phone || '—'}</div>
            </div>
          </div>
          <button type="button" onClick={clear} className="p-1 hover:bg-rose-50 rounded text-rose-500" data-testid={`${testid}-clear`}>
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            className="w-full pl-9 pr-3 py-2 border border-neutral-200 rounded-md outline-none focus:border-navy text-sm"
            data-testid={`${testid}-input`}
          />
          {open && (q.length > 0 || items.length > 0) && (
            <div className="absolute z-50 mt-1 left-0 right-0 bg-white border border-neutral-200 rounded-md shadow-lg max-h-80 overflow-y-auto">
              {loading && <div className="px-3 py-2 text-xs text-neutral-400">Searching…</div>}
              {!loading && items.length === 0 && q.length > 0 && (
                <div className="px-3 py-3 text-xs text-neutral-500">No match for "<span className="font-mono">{q}</span>". Try first name, employee number, email or phone.</div>
              )}
              {items.map((e) => (
                <button
                  type="button"
                  key={e.id}
                  onClick={() => pick(e)}
                  className="w-full text-left px-3 py-2 hover:bg-navy/5 border-b border-neutral-100 last:border-0"
                  data-testid={`${testid}-result-${e.employee_no || e.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium text-sm">{e.first_name} {e.last_name}</div>
                    <div className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-navy/10 text-navy">{e.employee_no || e.employee_code || '—'}</div>
                  </div>
                  <div className="text-[11px] text-neutral-500 mt-0.5">{e.email || '—'} · {e.phone || '—'} · <span className="font-mono">{e.employment_status}</span></div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
